import React, { useCallback, useMemo, useRef } from "react"
import { BadgeElement, BadgeElementType } from "sharedTypes/badge"
import EditorCanvas, { SelectionBox } from "components/CanvasEditor/EditorCanvas"
import { useSelection } from "components/CanvasEditor/Editor"
import { useBadgeTemplate } from "../hooks/useBadgeTemplate"
import { useBadgeSelection } from "../contexts/BadgeSelectionContext"
import { getTemplateDimensions } from "../utils/badgeHelpers"
import { EDITOR_RESOLUTION, OVERFLOW_EXTENT } from "../utils/constants"
import BadgeFrame from "./BadgeFrame"

type Box = { x: number; y: number; width: number; height: number }

const isHollowShape = (element: BadgeElement) => (
  (element.type === BadgeElementType.RECTANGLE || element.type === BadgeElementType.OVAL)
  && element.data.fillColor === "transparent"
)

const contains = (outer: Box, inner: Box) => (
  inner.x >= outer.x && inner.y >= outer.y
  && inner.x + inner.width <= outer.x + outer.width
  && inner.y + inner.height <= outer.y + outer.height
)

/** The editor's zoomable, pannable canvas with the badge on it. */
const BadgeCanvas = () => {
  const { currentTemplate } = useBadgeTemplate()
  const {
    selectedElementIds,
    selectMultiple,
    handleDeselectElement,
    inlineEditingElementId,
    stopInlineEditing,
  } = useBadgeSelection()

  // While the box is dragged, the elements it touches are outlined; on release they become
  // the selection, added to the previous one with Shift
  const setPreview = useSelection((state) => state.setPreview)
  const touchedByBox = useRef<string[]>([])
  const handleSelectionBoxChange = useCallback((box: SelectionBox | null, additive: boolean) => {
    if (!box) {
      const touched = touchedByBox.current
      touchedByBox.current = []
      setPreview([])
      selectMultiple(additive
        ? [...selectedElementIds, ...touched.filter((id) => !selectedElementIds.includes(id))]
        : touched)

      return
    }

    const boxMm = box.rect
    // A box drawn inside an unfilled rectangle or oval (a border) selects what's in it, not
    // the border
    touchedByBox.current = currentTemplate.badgeData.elements
      .filter((element) => box.ids.includes(element.id))
      .filter((element) => !(isHollowShape(element) && contains(element, boxMm)))
      .map((element) => element.id)
    setPreview(touchedByBox.current)
  }, [currentTemplate, selectedElementIds, selectMultiple, setPreview])

  const { width, height } = getTemplateDimensions(currentTemplate)
  const contentSize = useMemo(() => ({ width, height }), [width, height])

  // A mouse-down anywhere on the canvas ends inline text editing
  const endInlineEditing = useCallback(() => {
    if (!inlineEditingElementId) return

    const activeElement = document.activeElement as HTMLElement
    if (activeElement && activeElement.tagName === "INPUT") {
      activeElement.blur()
    }
    stopInlineEditing()
  }, [inlineEditingElementId, stopInlineEditing])

  return (
    <EditorCanvas
      contentSize={contentSize}
      padding={OVERFLOW_EXTENT / EDITOR_RESOLUTION}
      resetKey={currentTemplate.id}
      onBackgroundClick={handleDeselectElement}
      onSelectionBoxChange={handleSelectionBoxChange}
      onMouseDown={endInlineEditing}
    >
      <BadgeFrame />
    </EditorCanvas>
  )
}

export default BadgeCanvas
